import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useToast } from '@/foundation/toast/use-toast'
import { createQueue, deleteQueue, fetchAllQueues, updateQueue, updateQueuePositions } from '@/service/tickets'

type Queue = {
  id: string
  title: string
  slug: string
  description: string
  companyId: string
  filters: string
  position?: number
  show?: boolean
  teams?: { id: string; name: string }[]
}

export const useQueuesStore = defineStore('queues', () => {
  const queues = ref<Queue[]>([])
  const { toast } = useToast()
  const isLoadingQueues = ref(false)

  const setQueues = (newQueues: Queue[]) => {
    queues.value = newQueues ?? []
  }
  const fetchQueues = async () => {
    isLoadingQueues.value = true
    const { onSuccess, onError } = fetchAllQueues()

    return new Promise<void>((resolve) => {
      onSuccess((response) => {
        isLoadingQueues.value = false
        setQueues(response)
        resolve(response)
      })
      onError(() => {
        isLoadingQueues.value = false
        resolve()
      })
    })
  }

  const updateQueues = (id: string, data: any) => {
    return new Promise<void>((resolve, reject) => {
      const { onSuccess, onError } = updateQueue(id, data)

      onSuccess((response) => {
        const index = queues.value.findIndex((queue) => queue.id === id)

        if (index !== -1) {
          queues.value[index] = { ...queues.value[index], ...response }
        }
        toast({
          title: `Queue updated successfully`,
          description: `Queue "${response.title}" has been updated.`,
          variant: 'success',
        })
        resolve(response)
      })
      onError((error) => {
        reject(error)
      })
    })
  }
  const deleteQueues = (id: string) => {
    return new Promise<void>((resolve, reject) => {
      const { onSuccess, onError } = deleteQueue(id)

      onSuccess(() => {
        queues.value = queues.value.filter((queue) => queue.id !== id)
        resolve()
      })
      onError((error) => {
        reject(error)
      })
    })
  }
  const createQueues = (data: any) => {
    return new Promise<void>((resolve, reject) => {
      const { onSuccess, onError } = createQueue(data)

      onSuccess((response) => {
        queues.value.push(response)
        resolve(response)
      })
      onError((error) => {
        reject(error)
      })
    })
  }

  const updateTeamAffiliationInQueues = (teamId: string, teamName: string, prevQueueIds: string[], newQueueIds: string[]) => {
    prevQueueIds.forEach((qid) => {
      if (!newQueueIds.includes(qid)) {
        const queue = queues.value.find((q) => q.id === qid)

        if (queue) {
          if (!Array.isArray(queue.teams)) queue.teams = []
          queue.teams = (queue.teams as { id: string; name: string }[]).filter((t) => t.id !== teamId)
        }
      }
    })
    newQueueIds.forEach((qid) => {
      const queue = queues.value.find((q) => q.id === qid)

      if (queue) {
        if (!Array.isArray(queue.teams)) queue.teams = []
        if (!(queue.teams as { id: string; name: string }[]).some((t) => t.id === teamId)) {
          ;(queue.teams as { id: string; name: string }[]).push({ id: teamId, name: teamName })
        }
      }
    })
  }

  const updateQueuePositionsApi = async (positions: { id: string; position: number }[]) => {
    const { onSuccess, onError } = updateQueuePositions(positions)

    onSuccess(() => {
      positions.forEach(({ id, position }) => {
        const queue = queues.value.find((q) => q.id === id)

        if (queue) {
          queue.position = position
        }
      })
      queues.value.sort((a, b) => (a.position || 0) - (b.position || 0))
    })

    onError((error) => {
      console.error('Failed to update queue positions:', error)
    })
  }

  return {
    queues,
    setQueues,
    fetchQueues,
    updateQueues,
    deleteQueues,
    createQueues,
    isLoadingQueues,
    updateTeamAffiliationInQueues,
    updateQueuePositionsApi,
  }
})
